// DonationDriveCard — the "Keep Nitter alive!" funding-drive progress card
// used by the global DonationBanner (sticky/folding, mounted under the
// sticky header on the dashboard and the data-source page) and reusable
// inline elsewhere (e.g. a future About/Donate section).
//
//   ♥  Keep Nitter alive!  The next $147 in server bills is due Oct 26.
//   Servers, bandwidth, and the work of keeping these instances
//   reachable all cost money and effort. There are no ads and no
//   trackers here — donations keep it that way. If you can miss only
//   $5, that would help us out a lot.
//   ▓▓▓▓▓▓▓▓▓▓▓░░░░░
//   Raised $119 of $147 (81%) so far  [$5] [PP] Donate $5 with PayPal
//
// The card owns its own amount state — the editable amount input lives
// inside .donate-drive-foot next to the CTA, so every instance of the card
// gets the editable amount for free (each instance keeps its own state, by
// design — they don't sync).
//
// Click tracking: opening the PayPal link records the click in the shared
// personal record (settings.markDonateClicked — an INTENT signal, not a
// completed donation; there is no PayPal return callback here). The
// donation UI backs off for two weeks after a click, then quietly returns
// in case it never went through.

import { useState } from "preact/hooks";
import { PayPalMark } from "@/components/PayPalMark";
import { markDonateClicked } from "@/settings";
// noinspection ES6PreferShortImport
import {
  SUGGESTED_DONATION,
  formatDeadline,
  payPalDonateUrl,
  usd,
} from "@/lib/donate";
import type { FundingState } from "@/lib/donate";
import {NiceAmount, usdEquivalent, guessCurrency, defaultLocale, guessCurrencyOrUndefined, getFormatter} from "@/lib/currency";

export interface DonationDriveCardProps {
  /** Current drive (target / deadline / raised). Drives are derived from a deterministic monotonic curve, so `raised` advances on its own as the deadline approaches, see @shared/donate. */
  drive: FundingState;
}
function convertStringToNiceAmount(v: string | null | undefined, previousAmount: NiceAmount | null): NiceAmount | null {
    const amount = v && Number(v)
    if (amount) {
        if(previousAmount) {
            return { ...previousAmount, amount };
        } else {
            const locale: string | undefined = defaultLocale();
            const currency: string = guessCurrencyOrUndefined(locale) || "USD";
            return { amount, currency, text: getFormatter(locale, currency || "USD", 0).format(amount) };
        }
    } else {
        return null;
    }
}
export function DonationDriveCard({ drive }: DonationDriveCardProps) {
  // Editable donation amount — bare string (no `$`), fed straight into the paypal.me URL via payPalDonateUrl. Default = SUGGESTED_DONATION ("5");
  // the user can type any whole-dollar or decimal amount. Both the CTA's href and its label track this state, so editing the field updates the destination + label live.
  const [amount, setAmount] = useState<NiceAmount | null>(null);
  // Fallback to the suggested value when the field is empty or non-numeric, so the CTA label never reads "Donate $NaN with PayPal".
    let amountToUse: number
    let niceAmount: NiceAmount// = usdEquivalent(amnt);
    if (amount)
        niceAmount = amount;
    else {
        const locale: string | undefined = defaultLocale();
        const currency: string | undefined = guessCurrencyOrUndefined(locale);
        // todo do this once on initialization and not every refresh as long as [amount] is `null`?
        niceAmount = usdEquivalent(SUGGESTED_DONATION, {locale, currency});
    }
  const labelAmount = niceAmount.text;
  const donateUrl = payPalDonateUrl(niceAmount.amount, niceAmount.currency);

  const pct = Math.min(100, Math.max(1, Math.round((drive.raised / drive.target) * 100)));
  const deadlineStamp = new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(drive.deadline));

  return (
    <div class="donate-drive-card">
      <div class="donate-drive-head">
        <span class="donate-drive-badge" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
          </svg>
        </span>
        <p class="donate-drive-title">
          <strong>Keep Nitter alive!</strong>{" "}
          The next {usd(drive.target)} in server bills is due{" "}
          {formatDeadline(drive.deadline)}.
        </p>
      </div>

      <p class="donate-drive-copy">
        Servers, bandwidth, and the work of keeping these instances reachable
        all cost money and effort. There are no ads and no trackers here.

          Donations keep it that way. If you can miss only {usd(SUGGESTED_DONATION)}, that would help us out a lot.
      </p>

      <div
        class="donate-drive-progress"
        role="progressbar"
        aria-label="Funding progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        title={`${pct}% of this drive's target`}
      >
        <div class="donate-drive-progress-fill" style={`width:${pct}%`} />
      </div>

      <div class="donate-drive-foot">
        <span class="donate-drive-stats">
          Raised <strong>{usd(drive.raised)}</strong> of{" "}
          <strong>{usd(drive.target)}</strong> ({pct}%) so far
        </span>
        {/* CTA group: narrow $-prefixed amount input + the PayPal button,
            on the right side of the foot (space-between pushes stats left,
            this group right). The input is the source of truth for both
            the CTA's href (paypal.me/<user>/<amount>) and its label. */}
        <div class="donate-drive-cta-group">
          <span class="donate-amount-field">
            <span class="donate-amount-prefix" aria-hidden="true">$</span>
            <input
              class="donate-amount-input"
              type="number"
              min="1"
              step="1"
              inputMode="decimal"
              value={niceAmount.amount}
              onInput={(e: Event) => {
                  const newAmount = convertStringToNiceAmount((e.target as HTMLInputElement).value, amount)
                  //if (newAmount)
                  setAmount(newAmount)
              }}
              aria-label="Donation amount in USD"
              title="Donation amount — PayPal opens with this prefilled"
              size={4}
            />
          </span>
          <a
            class="donate-drive-cta"
            href={donateUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={`PayPal opens at paypal.me with ${amount || "no amount"} — change it to anything`}
            onClick={() => markDonateClicked()}
          >
            <PayPalMark size={15} />
            Donate {labelAmount} with PayPal
          </a>
        </div>
      </div>

      {/* Screen-reader-only completeness: the exact due date. */}
      <span class="sr-only">
        Bills for servers, bandwidth, and the work of keeping these
        instances reachable are due {deadlineStamp}.
      </span>
    </div>
  );
}
